import { useContext } from "react";

import { useTranslation } from "react-i18next";

import moment from "moment-timezone";

import LanguageContext from "../../context/language/LanguageContext";
import { DATE_FORMATS, DateType, LANGUAGES } from "../types";
import { useDateTime } from "./useDateTime";

export const useRelativeDateTime = () => {
  const { language } = useContext(LanguageContext);

  const { currentTimeZone, checkIfDateIsNull } = useDateTime();

  const { t } = useTranslation();

  const formatRelativeDateTime = (date: DateType, now: moment.Moment) => {
    if (checkIfDateIsNull(date) || date === "undefined") {
      return "-";
    }

    const targetDate = moment
      .utc(date)
      .tz(currentTimeZone)
      .locale(language ?? LANGUAGES.English);

    if (!targetDate.isValid()) {
      return "-";
    }

    const localNow = now.clone().tz(currentTimeZone);
    const time = targetDate.format(DATE_FORMATS.HoursMinutes);

    // Difference between calendar days, not a rolling 24-hour period.
    const dayDiff = localNow
      .clone()
      .startOf("day")
      .diff(targetDate.clone().startOf("day"), "days");

    if (dayDiff === 0) return `${t("Dialog##past dates##today")}, ${time}`;
    if (dayDiff === 1) return `${t("Dialog##past dates##yesterday")}, ${time}`;
    if (dayDiff <= 7) return `${targetDate.format("ddd D")}, ${time}`;

    const weeks = Math.floor(dayDiff / 7);
    if (weeks === 1) return t("Dialog##past dates##1 week ago");
    if (weeks < 5)
      return t("Dialog##past dates##multiple weeks ago", { weekCount: weeks });

    const months = Math.max(1, localNow.diff(targetDate, "months"));
    if (months === 1) return t("Dialog##past dates##1 month ago");
    if (months < 12)
      return t("Dialog##past dates##multiple months ago", {
        monthCount: months
      });

    const years = Math.max(1, localNow.diff(targetDate, "years"));
    if (years === 1) return t("Dialog##past dates##1 year ago");
    return t("Dialog##past dates##multiple years ago", { yearCount: years });
  };

  const formatRelativeFutureDateTime = (date: DateType, now: moment.Moment) => {
    if (checkIfDateIsNull(date) || date === "undefined") {
      return "-";
    }

    const targetDate = moment
      .utc(date)
      .tz(currentTimeZone)
      .locale(language ?? LANGUAGES.English);

    if (!targetDate.isValid()) {
      return "-";
    }

    const localNow = now.clone().tz(currentTimeZone);
    const totalMinutes = targetDate.diff(localNow, "minutes");

    if (totalMinutes < 60) {
      return formatFutureMinutes(totalMinutes);
    }

    if (totalMinutes < 24 * 60) {
      return formatFutureHoursAndMinutes(totalMinutes);
    }

    const totalDays = targetDate.diff(localNow, "days");

    if (totalDays < 7) {
      return formatFutureDays(totalDays);
    }

    const weekCount = Math.floor(totalDays / 7);

    if (weekCount < 5) {
      return formatFutureWeeks(weekCount);
    }

    const monthCount = targetDate.diff(localNow, "months");

    if (monthCount < 12) {
      return formatFutureMonths(monthCount);
    }

    return formatFutureYears(targetDate.diff(localNow, "years"));
  };

  const formatFutureMinutes = (minutesCount: number) => {
    if (minutesCount === 0) {
      return t("Dialog##future dates##less than a minute");
    }

    if (minutesCount === 1) {
      return t("Dialog##future dates##1 minute");
    }

    return t("Dialog##future dates##multiple minutes", {
      minutesCount
    });
  };

  const formatFutureHoursAndMinutes = (totalMinutes: number) => {
    const hourCount = Math.floor(totalMinutes / 60);
    const minutesCount = totalMinutes % 60;

    return t("Dialog##future dates##hours and minutes", {
      hourCount,
      minutesCount
    });
  };

  const formatFutureDays = (daysCount: number) => {
    if (daysCount === 1) {
      return t("Dialog##future dates##1 day");
    }

    return t("Dialog##future dates##multiple days", {
      daysCount
    });
  };

  const formatFutureWeeks = (weeksCount: number) => {
    if (weeksCount === 1) {
      return t("Dialog##future dates##1 week");
    }

    return t("Dialog##future dates##multiple weeks", {
      weeksCount
    });
  };

  const formatFutureMonths = (monthsCount: number) => {
    if (monthsCount === 1) {
      return t("Dialog##future dates##1 month");
    }

    return t("Dialog##future dates##multiple months", {
      monthsCount
    });
  };

  const formatFutureYears = (yearsCount: number) => {
    if (yearsCount === 1) {
      return t("Dialog##future dates##1 year");
    }

    return t("Dialog##future dates##multiple years", {
      yearsCount
    });
  };

  const getDifferenceFromCurrentUTCInMinutes = (
    utcString: string | null,
    now: moment.Moment
  ) => {
    const targetUTC = moment.utc(utcString);

    return targetUTC.isValid()
      ? now.clone().utc().diff(targetUTC, "minutes")
      : undefined;
  };

  return {
    formatRelativeDateTime,
    getDifferenceFromCurrentUTCInMinutes,
    formatRelativeFutureDateTime
  };
};
